import Finance360Ops from './pages/facturacion/ops/Finance360Ops';
import RequiereModulo from './components/RequiereModulo';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useAuthStore from './store/authStore';
import Layout from './components/Layout';
import SinEmpresa from './components/SinEmpresa';
import Inicio from './sitio/Inicio';
import Producto from './sitio/Producto';
import Precios from './sitio/Precios';
import EstadoSitio from './sitio/Estado';
import DemoSitio from './sitio/Demo';
import Legal from './sitio/Legal';
import RecorridoSitio from './sitio/Recorrido';
import Login from './pages/Login';
import Impersonar from './pages/Impersonar';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import EmbedShell from './pages/embed/EmbedShell';
import EntrarDesdeUnity from './pages/suite/EntrarDesdeUnity';

// SuperAdmin App
import SuperadminApp from './superadmin/App';

// Facturación pages
import CrearFactura from './pages/facturacion/CrearFactura';
import SeleccionarDocumento from './pages/facturacion/SeleccionarDocumento';
import HistorialFacturas from './pages/facturacion/HistorialFacturas';
import FacturacionApi from './pages/facturacion/FacturacionApi';
import NotasCredito from './pages/facturacion/NotasCredito';
import NotasDebito from './pages/facturacion/NotasDebito';
import FacturacionRecurrente from './pages/facturacion/FacturacionRecurrente';
import Cotizaciones from './pages/facturacion/Cotizaciones';
import CrearCotizacion from './pages/facturacion/CrearCotizacion';
import OrdenesCompra from './pages/compras/OrdenesCompra';
import CrearOrdenCompra from './pages/compras/CrearOrdenCompra';

// Reportes pages
import Reportes from './pages/reportes/Reportes';
import ReportesPersonalizados from './pages/reportes/ReportesPersonalizados';

// Configuración pages
import ListaEmpresas from './pages/configuracion/ListaEmpresas';
import ConfiguracionEmpresa from './pages/configuracion/ConfiguracionEmpresa';

// Clientes pages
import ListaClientes from './pages/clientes/ListaClientes';
import FormularioCliente from './pages/clientes/FormularioCliente';
import ImportarClientes from './pages/clientes/ImportarClientes';

// Productos pages
import ListaProductos from './pages/productos/ListaProductos';
import FormularioProducto from './pages/productos/FormularioProducto';
import PlanillasEmbed from './pages/planillas/PlanillasEmbed';
import ImportarProductos from './pages/productos/ImportarProductos';

// Gastos / CxP pages
import FacturasRecibidas from './pages/gastos/FacturasRecibidas';
import PendientesRechazo from './pages/gastos/PendientesRechazo';
import DetalleFactura from './pages/gastos/DetalleFactura';
import HistoricoPagos from './pages/gastos/HistoricoPagos';
import PendientesPorProveedor from './pages/gastos/PendientesPorProveedor';
import LotesPago from './pages/gastos/LotesPago';
import PagosBanco from './pages/gastos/PagosBanco';
import Proveedores from './pages/gastos/Proveedores';
import EmailImport from './pages/gastos/EmailImport';
import IVAInteligente from './pages/gastos/IVAInteligente';
import FacturaCompraEmitida from './pages/gastos/FacturaCompraEmitida';

// CxC (Cuentas por Cobrar) pages
import FacturasPendientes from './pages/cxc/FacturasPendientes';
import PendientesPorCliente from './pages/cxc/PendientesPorCliente';
import HistoricoPagosCxC from './pages/cxc/HistoricoPagosCxC';

// Parámetros del sistema
import Parametros from './pages/parametros/Parametros';

// Usuarios y Roles
import Usuarios from './pages/configuracion/Usuarios';
import Roles from './pages/configuracion/Roles';
import EstadoSistema from './pages/configuracion/EstadoSistema';
import Respaldos from './pages/configuracion/Respaldos';
import Apariencia from './pages/configuracion/Apariencia';
import ApiCredentials from './pages/configuracion/ApiCredentials';
import Integraciones from './pages/configuracion/Integraciones';

// Contabilidad
import ContabilidadModule from './pages/contabilidad';
// Presupuesto
import Presupuesto from './pages/presupuesto/Presupuesto';
import PresupuestoConsolidado from './pages/presupuesto/Consolidado';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { token, currentEmisor } = useAuthStore();

  if (!token) {
    return <Navigate to="/" replace />;
  }

  // Sin empresa elegida no hay Layout: la pantalla deja elegir una o crear la primera
  if (!currentEmisor) {
    return <SinEmpresa />;
  }

  return children;
}

// Public route wrapper (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { token } = useAuthStore();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Home route - Landing or Login based on domain
function HomeRoute() {
  const { token } = useAuthStore();

  if (token) {
    return <Navigate to="/dashboard" replace />;
  }

  const hostname = window.location.hostname;

  // En app.finance360.cr mostrar Login directamente
  if (hostname === 'app.finance360.cr') {
    return <Navigate to="/login" replace />;
  }

  // Subdominios de tenants (*.finance360.cr excepto www, api, app) → Login
  if (hostname.endsWith('.finance360.cr')) {
    const subdomain = hostname.split('.')[0];
    if (!['www', 'api', 'app'].includes(subdomain)) {
      // Es un tenant subdomain, guardar el slug y mostrar login
      localStorage.setItem('tenantSlug', subdomain);
      return <Navigate to="/login" replace />;
    }
  }

  // Localhost con subdomain también (para desarrollo)
  if (hostname !== 'localhost' && hostname.includes('.localhost')) {
    const subdomain = hostname.split('.')[0];
    localStorage.setItem('tenantSlug', subdomain);
    return <Navigate to="/login" replace />;
  }

  return <Inicio />;
}

export default function App() {
  const location = useLocation();

  // If accessing /superadmin routes, render SuperAdmin app
  if (location.pathname.startsWith('/superadmin')) {
    return <SuperadminApp />;
  }

  return (
    <Routes>
      {/* Home - Landing or redirect to dashboard */}
      <Route path="/" element={<HomeRoute />} />

      {/* Sitio web público (finance360.cr): inglés por omisión, con español */}
      <Route path="/product" element={<Producto />} />
      <Route path="/pricing" element={<Precios />} />
      <Route path="/status" element={<EstadoSitio />} />
      <Route path="/demo" element={<DemoSitio />} />
      <Route path="/tour" element={<RecorridoSitio />} />
      <Route path="/privacy" element={<Legal doc="privacidad" />} />
      <Route path="/terms" element={<Legal doc="terminos" />} />

      {/* Pantalla abierta dentro de otro producto (Unity 360).
          Va FUERA de `ProtectedRoute` y de `Layout` a propósito: no lleva login
          —la sesión llega por boleto de un solo uso— ni menú ni encabezado, que
          es lo que hace que se vea parte del anfitrión. `pages/embed/EmbedShell`. */}
      <Route path="/embed/:pantalla" element={<EmbedShell />} />

      {/* El pasillo desde el menu de portales de Unity 360 (item 058). FUERA de
          `ProtectedRoute` por la misma razon que el embebido: la sesion llega
          por el pase firmado, no por un login. Pero a diferencia del embebido,
          esto abre Finance ENTERO y su sesion se guarda, porque la persona viene
          a trabajar aca. `pages/suite/EntrarDesdeUnity`. */}
      <Route path="/entrar-desde-unity" element={<EntrarDesdeUnity />} />

      {/* Public routes */}
      <Route path="/impersonar" element={<Impersonar />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />

      {/* Protected routes with Layout */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="dashboard" element={<Dashboard />} />

        {/* Facturación */}
        <Route path="facturacion">
          <Route path="nueva" element={<CrearFactura />} />
          <Route path="seleccionar" element={<SeleccionarDocumento />} />
          <Route path="historial" element={<HistorialFacturas />} />
          <Route path="api-drafts" element={<FacturacionApi />} />
          <Route path="notas-credito" element={<NotasCredito />} />
          <Route path="notas-debito" element={<NotasDebito />} />
          <Route path="recurrentes" element={<FacturacionRecurrente />} />
          <Route path="ops" element={<RequiereModulo modulo="ops"><Finance360Ops /></RequiereModulo>} />
          <Route path="cotizaciones" element={<Cotizaciones />} />
          <Route path="cotizaciones/nueva" element={<CrearCotizacion />} />
          <Route path="cotizaciones/:id/editar" element={<CrearCotizacion />} />
        </Route>

        {/* Compras */}
        <Route path="compras">
          <Route path="ordenes-compra" element={<OrdenesCompra />} />
          <Route path="ordenes-compra/nueva" element={<CrearOrdenCompra />} />
          <Route path="ordenes-compra/desde-cotizacion/:cotizacionId" element={<CrearOrdenCompra />} />
          <Route path="ordenes-compra/:id/editar" element={<CrearOrdenCompra />} />
        </Route>

        {/* Presupuesto */}
        <Route path="presupuesto" element={<Presupuesto />} />
        <Route path="presupuesto/consolidado" element={<PresupuestoConsolidado />} />
        <Route path="presupuesto/:id" element={<Presupuesto />} />

        {/* Reportes */}
        <Route path="reportes">
          <Route index element={<Reportes />} />
          <Route path="personalizados" element={<ReportesPersonalizados />} />
        </Route>

        {/* Clientes */}
        <Route path="clientes">
          <Route index element={<ListaClientes />} />
          <Route path="nuevo" element={<FormularioCliente />} />
          <Route path="importar" element={<ImportarClientes />} />
          <Route path=":id/editar" element={<FormularioCliente />} />
        </Route>

        {/* Productos */}
        <Route path="productos">
          <Route index element={<ListaProductos />} />
          <Route path="nuevo" element={<FormularioProducto />} />
          <Route path="importar" element={<ImportarProductos />} />
          <Route path=":id/editar" element={<FormularioProducto />} />
        </Route>

        {/* Planillas — embed del sistema externo K360 */}
        <Route path="planillas" element={<PlanillasEmbed />} />

        {/* Gastos - Recepción de facturas */}
        <Route path="gastos">
          <Route path="bandeja" element={<EmailImport />} />
          <Route path="por-confirmar" element={<FacturasRecibidas key="por_confirmar" estado="por_confirmar" />} />
          <Route path="pendientes-rechazo" element={<PendientesRechazo />} />
          <Route path="aceptadas" element={<FacturasRecibidas key="aceptada" estado="aceptada" />} />
          <Route path="factura/:id" element={<DetalleFactura />} />
          <Route path="iva" element={<IVAInteligente />} />
          <Route path="fec" element={<FacturaCompraEmitida />} />
        </Route>

        {/* Cuentas x Cobrar (CxC) - Facturas emitidas pendientes de cobro */}
        <Route path="cxc">
          <Route path="pendientes" element={<FacturasPendientes />} />
          <Route path="por-cliente" element={<PendientesPorCliente />} />
          <Route path="aplicar-pago" element={<FacturasPendientes />} />
          <Route path="historico" element={<HistoricoPagosCxC />} />
        </Route>

        {/* Cuentas x Pagar (CxP) - Solo facturas aceptadas */}
        <Route path="cxp">
          <Route path="facturas" element={<FacturasRecibidas estado="por_pagar" />} />
          <Route path="por-proveedor" element={<PendientesPorProveedor />} />
          {/* Lotes de pago con aprobación (S7) */}
          <Route path="lotes" element={<LotesPago />} />
          {/* Pagos desde el archivo del banco (S8) */}
          <Route path="pagos-banco" element={<PagosBanco />} />
          <Route path="historico-pagos" element={<HistoricoPagos />} />
          <Route path="proveedores" element={<Proveedores />} />
        </Route>

        {/* Contabilidad */}
        <Route path="contabilidad/*" element={<ContabilidadModule />} />

        {/* Configuración */}
        <Route path="configuracion">
          <Route index element={<ListaEmpresas />} />
          <Route path="empresa" element={<ListaEmpresas />} />
          <Route path="empresa/:id" element={<ConfiguracionEmpresa />} />
          <Route path="parametros" element={<Parametros />} />
          <Route path="usuarios" element={<Usuarios />} />
          <Route path="roles" element={<Roles />} />
          <Route path="api-credentials" element={<ApiCredentials />} />
          <Route path="integraciones" element={<Integraciones />} />
          <Route path="estado-sistema" element={<EstadoSistema />} />
          <Route path="respaldos" element={<Respaldos />} />
          <Route path="apariencia" element={<Apariencia />} />
        </Route>
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
